/**
 * The canonical **skin-tone palette** for character creation - the single source
 * of truth both the client splash UI and the authoritative server import, so the
 * set of legitimate body skin hues can never drift between "what the client offers"
 * and "what the server accepts" (ADR-0188).
 *
 * A player's body skin hue is chosen **once**, at creation, and then persisted as
 * the account's **immutable** identity (`server/accountStore.ts` -> `setIdentity`,
 * settable once, never changeable). That immutability is exactly why the set must
 * be server-authoritative: a crafted `Login` can carry any positive-integer hue,
 * and before ADR-0188 the server took it verbatim, permanently branding the account
 * with an out-of-palette colour. The server now gates the creation hue against
 * {@link isSkinHue}; a non-member is rejected down to {@link DEFAULT_SKIN_HUE}
 * (see `server/identity.ts` -> `coerceHue`).
 *
 * This lives in the sim layer (not `src/splash.ts`) because the server must not
 * depend on a client-only, DOM-touching module. The module is pure data - it reads
 * no Pixi, no DOM, and no clock - so it imports cleanly into the headless server
 * (the sim/presentation separation the architecture test enforces).
 *
 * The `css` values are **approximate previews only** for the splash swatches; the
 * authoritative recolour is the baked `hues.mul` ramp applied at render time. Every
 * hue id is **VERIFY**-flagged: there is no `hues.mul`/client on this host to
 * resolve them against (the skin band ~1002-1058 follows the existing palette and
 * the registry's documented comments). The first entry (`1009`, the historical
 * default) stays the default skin hue so existing links/behaviour are unchanged.
 */

/** One skin-tone swatch offered at character creation: a UO hue id + CSS preview. */
export interface SkinSwatch {
  /** 1-based UO `hues.mul` id - the authoritative, persisted skin hue. */
  readonly hue: number;
  /** Approximate CSS colour previewing the hue; not the authoritative recolour. */
  readonly css: string;
}

/**
 * Body skin tones - the only skin palette character creation offers. The client
 * splash builds its swatch row from this list; the server validates the requested
 * creation hue against {@link SKIN_HUES}, so the two can never disagree.
 */
export const SKIN_SWATCHES: readonly SkinSwatch[] = [
  { hue: 1009, css: '#d8a878' }, // warm (default)
  { hue: 1002, css: '#f0c8a0' }, // pale
  { hue: 1018, css: '#c08a5a' }, // tan
  { hue: 1023, css: '#9c6b42' }, // brown
  { hue: 1042, css: '#7a4e30' }, // deep
  { hue: 1058, css: '#5a3a24' }, // darkest
];

/**
 * The default skin hue - the first (historical) swatch, `1009`. Used both as the
 * splash's pre-selected tone and as the server's **deterministic** fallback when a
 * requested creation hue is not a legitimate skin tone (ADR-0188).
 */
export const DEFAULT_SKIN_HUE: number = SKIN_SWATCHES[0]!.hue;

/** The membership set of legitimate creation skin hues, derived from the swatches. */
export const SKIN_HUES: ReadonlySet<number> = new Set(SKIN_SWATCHES.map((s) => s.hue));

/**
 * Is `hue` one of the legitimate creation skin tones? The server's authority on
 * which body skin hue a `Login` may set as the account's immutable identity.
 */
export function isSkinHue(hue: number): boolean {
  return SKIN_HUES.has(hue);
}
